"use client";

import Link from "next/link";
import {
  Activity, AlertTriangle, ArrowRight, BadgeCheck, BarChart3, BookOpen, Bot, Brain, Building2, Calendar, Clock, Code2, Compass, Cpu, Database, Eye, FileBarChart, FileText, Flame, GitBranch, GitCompare, Globe, Globe2, HandHeart, Heart, HeartPulse, Landmark, Languages, Layers, LayoutDashboard, LayoutGrid, LineChart, Map, Megaphone, Network, PenTool, Plug, Radio, Rocket, Shield, ShieldCheck, Siren, Sparkles, Target, Telescope, TrendingUp, User, Users, Vote, Zap, Boxes, Gauge, Tag,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";

import { Section, Container } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { GlassCard } from "@/components/ui/glass-card";
import { Reveal } from "@/components/ui/reveal";
import { Icon } from "@/components/ui/icon";
import { Counter } from "@/components/ui/counter";

import {
  hero, execSummary, problem, solution, platformOverviewSection,
  howItWorks, capabilities, engines, tiers, metrics, features,
  advancedFeatures, process, deliverables, outcomes, benefits, usps,
  audiences, industries, whyChooseUs, faq, paa, ctaSection, relatedPages,
  platformOverviewMeta,
} from "@/lib/data/platform-overview";

/* ============================================================
   1. HERO
   ============================================================ */
function Hero() {
  return (
    <Section id="top" className="pt-24 pb-12 sm:pt-28 sm:pb-16">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-20 left-1/4 h-80 w-80 rounded-full bg-[var(--color-crimson)] opacity-25 blur-[140px] animate-pulse-glow" />
        <div className="absolute top-44 right-1/4 h-96 w-96 rounded-full bg-[var(--color-purple)] opacity-25 blur-[150px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
      </div>
      <Container>
        <div className="max-w-4xl">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass text-xs font-semibold uppercase tracking-[0.28em]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
              </span>
              {hero.eyebrow}
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 text-balance text-[clamp(2.5rem,6vw,4.6rem)] font-bold leading-[0.98] tracking-[-0.03em]">
              {hero.titleLead}{" "}
              <span className="relative text-gradient animate-gradient-pan">
                {hero.titleAccent}
                <span className="pointer-events-none absolute -inset-x-2 -inset-y-1 bg-[var(--color-crimson)] opacity-20 blur-2xl -z-10" />
              </span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-[1.15rem] sm:text-[1.25rem] leading-relaxed text-[rgb(var(--fg-muted))] max-w-3xl">
              {hero.lede}
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link href={hero.ctaPrimary.href} className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base">
                {hero.ctaPrimary.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link href={hero.ctaSecondary.href} className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base">
                {hero.ctaSecondary.label}
              </Link>
            </div>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[rgb(var(--fg-subtle))]">
              {hero.indicators.map((ind, i) => (
                <span key={ind} className="flex items-center gap-2">
                  {i > 0 && <span className="h-1 w-1 rounded-full bg-[rgb(var(--border))]" />}
                  <span className="font-mono-num font-semibold text-[rgb(var(--fg))]">{ind}</span>
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   2. EXECUTIVE SUMMARY
   ============================================================ */
function ExecutiveSummary() {
  return (
    <Section id="exec-summary">
      <Container>
        <SectionHeading
          eyebrow="Executive Summary"
          icon="Award"
          title={execSummary.title}
          subtitle="The platform, the scale, the outcomes — in one view."
        />
        <div className="mt-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
          <Reveal>
            <GlassCard pad="lg" tone="glow" className="h-full">
              <h3 className="text-xl font-bold leading-snug mb-3">{execSummary.differentiator.title}</h3>
              <p className="text-[0.95rem] leading-relaxed text-[rgb(var(--fg-muted))]">
                {execSummary.differentiator.body}
              </p>
              <div className="mt-6 h-px bg-gradient-to-r from-transparent via-[var(--color-crimson)] to-transparent opacity-50" />
              <p className="mt-6 text-[0.92rem] leading-relaxed text-[rgb(var(--fg-muted))]">
                {execSummary.body}
              </p>
            </GlassCard>
          </Reveal>
          <Reveal delay={120}>
            <GlassCard pad="lg" tone="glow" className="h-full" style={{ ["--glow" as string]: "var(--color-emerald)" }}>
              <h3 className="text-xl font-bold leading-snug mb-4">{execSummary.impact.title}</h3>
              <p className="text-[0.92rem] leading-relaxed text-[rgb(var(--fg-muted))] mb-6">
                {execSummary.impact.body}
              </p>
              <div className="grid gap-3">
                {execSummary.impact.metrics.map((m) => (
                  <div key={m.label} className="flex items-center gap-4 rounded-xl border border-[rgb(var(--border)/0.18)] bg-[rgb(var(--surface)/0.4)] p-3">
                    <div
                      className="text-2xl font-bold font-mono-num leading-none"
                      style={{ color: m.accent }}
                    >
                      {m.value}
                    </div>
                    <div className="text-sm text-[rgb(var(--fg-muted))]">{m.label}</div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   3. THE PROBLEM
   ============================================================ */
function Problem() {
  return (
    <Section id="problem">
      <Container>
        <SectionHeading
          eyebrow="The Problem"
          icon="AlertTriangle"
          title={problem.title}
          subtitle={problem.intro}
        />
        <div className="mt-14 grid sm:grid-cols-2 gap-5">
          {problem.cards.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 100}>
              <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: c.accent }}>
                <div className="flex items-start gap-4">
                  <span
                    className="grid place-items-center h-12 w-12 rounded-xl shrink-0"
                    style={{ background: `linear-gradient(135deg, ${c.accent}26, ${c.accent}0a)`, boxShadow: `inset 0 0 0 1px ${c.accent}40, 0 6px 20px -8px ${c.accent}66` }}
                  >
                    <Icon name={c.icon} className="h-5 w-5" style={{ color: c.accent }} />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold leading-snug mb-2">{c.title}</h3>
                    <p className="text-[0.92rem] leading-relaxed text-[rgb(var(--fg-muted))]">{c.body}</p>
                  </div>
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   4. THE SOLUTION
   ============================================================ */
function Solution() {
  return (
    <Section id="solution">
      <Container>
        <SectionHeading
          eyebrow="The Solution"
          icon="Sparkles"
          title={solution.title}
          subtitle={solution.intro}
        />
        <div className="mt-14 grid sm:grid-cols-2 gap-5">
          {solution.pillars.map((p, i) => (
            <Reveal key={p.title} delay={(i % 2) * 100}>
              <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: p.accent }}>
                <div className="flex items-start gap-4">
                  <span
                    className="grid place-items-center h-12 w-12 rounded-xl shrink-0"
                    style={{ background: `linear-gradient(135deg, ${p.accent}26, ${p.accent}0a)`, boxShadow: `inset 0 0 0 1px ${p.accent}40, 0 6px 20px -8px ${p.accent}66` }}
                  >
                    <Icon name={p.icon} className="h-5 w-5" style={{ color: p.accent }} />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold leading-snug mb-2">{p.title}</h3>
                    <p className="text-[0.92rem] leading-relaxed text-[rgb(var(--fg-muted))]">{p.body}</p>
                  </div>
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   5. PLATFORM OVERVIEW (engines table)
   ============================================================ */
function PlatformOverviewSection() {
  return (
    <Section id="platform-overview">
      <Container>
        <SectionHeading
          eyebrow="Platform Overview"
          icon="LayoutDashboard"
          title={platformOverviewSection.title}
          subtitle={platformOverviewSection.intro}
        />

        {/* Engines table */}
        <Reveal className="mt-12">
          <GlassCard pad="lg" tone="glow" className="overflow-hidden p-0">
            <div className="px-6 pt-6 pb-3">
              <h3 className="text-lg font-bold">Four Proprietary AI Engines</h3>
              <p className="text-sm text-[rgb(var(--fg-muted))] mt-1">
                Purpose-built for each phase of the perception amplification lifecycle
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)]">
                    <th className="text-left font-semibold px-6 py-3 text-[0.72rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Engine</th>
                    <th className="text-left font-semibold px-6 py-3 text-[0.72rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Function</th>
                    <th className="text-left font-semibold px-6 py-3 text-[0.72rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Key Capability</th>
                  </tr>
                </thead>
                <tbody>
                  {platformOverviewSection.engines.map((e) => (
                    <tr key={e.name} className="border-b border-[rgb(var(--border)/0.08)] last:border-0 hover:bg-[rgb(var(--border)/0.04)] transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-semibold" style={{ color: e.accent }}>{e.name}</span>
                      </td>
                      <td className="px-6 py-4 text-[rgb(var(--fg-muted))]">{e.function}</td>
                      <td className="px-6 py-4 text-[rgb(var(--fg-muted))]">{e.key}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </Reveal>

        {/* Callouts */}
        <div className="mt-6 grid sm:grid-cols-3 gap-5">
          {platformOverviewSection.callouts.map((c, i) => (
            <Reveal key={c.title} delay={i * 100}>
              <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: "var(--color-cyan)" }}>
                <Icon name={c.icon} className="h-6 w-6 text-[var(--color-cyan)] mb-3" />
                <h3 className="font-semibold text-base mb-1.5">{c.title}</h3>
                <p className="text-[0.88rem] leading-relaxed text-[rgb(var(--fg-muted))]">{c.body}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   6. HOW IT WORKS (timeline)
   ============================================================ */
function HowItWorksSection() {
  return (
    <Section id="how-it-works">
      <Container>
        <SectionHeading
          eyebrow="How It Works"
          icon="Workflow"
          title={howItWorks.title}
          subtitle={howItWorks.intro}
        />
        <div className="mt-14 relative">
          {/* Connector line */}
          <div className="absolute left-7 sm:left-9 top-0 bottom-0 w-px bg-gradient-to-b from-[var(--color-crimson)] via-[var(--color-purple)] to-[var(--color-cyan)] opacity-30" />
          <div className="grid gap-6">
            {howItWorks.phases.map((p, i) => (
              <Reveal key={p.num} delay={i * 60}>
                <div className="relative flex items-start gap-5 sm:gap-7 pl-2 sm:pl-4">
                  <div
                    className="relative shrink-0 grid place-items-center h-14 w-14 sm:h-18 sm:w-18 rounded-2xl"
                    style={{
                      background: `linear-gradient(135deg, ${p.accent}33, ${p.accent}11)`,
                      boxShadow: `inset 0 0 0 1px ${p.accent}50, 0 6px 24px -10px ${p.accent}99`,
                    }}
                  >
                    <Icon name={p.icon} className="h-6 w-6" style={{ color: p.accent }} />
                  </div>
                  <div className="flex-1 min-w-0 pt-1.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[0.7rem] uppercase tracking-[0.18em] font-semibold" style={{ color: p.accent }}>Phase {p.num}</span>
                      <h3 className="text-lg font-bold leading-snug">{p.title}</h3>
                    </div>
                    <p className="mt-2 text-[0.93rem] leading-relaxed text-[rgb(var(--fg-muted))]">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   7. CORE CAPABILITIES
   ============================================================ */
function Capabilities() {
  return (
    <Section id="capabilities">
      <Container>
        <SectionHeading
          eyebrow="Core Capabilities"
          icon="Boxes"
          title="The Complete Perception Amplification Toolkit"
          subtitle="Eight capabilities that work in concert for autonomous, comprehensive perception management."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {capabilities.map((c, i) => {
            const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-coral)", "var(--color-denim)", "var(--color-violet)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={c.title} delay={(i % 4) * 60}>
                <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <Icon name={c.icon} className="h-6 w-6 mb-3" style={{ color: a }} />
                  <h3 className="font-semibold text-base leading-snug mb-1.5">{c.title}</h3>
                  <p className="text-[0.85rem] leading-relaxed text-[rgb(var(--fg-muted))]">{c.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   8. INTERNAL ENGINES
   ============================================================ */
function Engines() {
  return (
    <Section id="internal-engines">
      <Container>
        <SectionHeading
          eyebrow="Internal Engines"
          icon="Cpu"
          title="Four Proprietary AI Engines. One Autonomous Platform."
          subtitle="Interconnected engines, each purpose-built. Together, they deliver autonomous perception amplification at a scale impossible for human teams or conventional platforms."
        />
        <div className="mt-14 grid lg:grid-cols-2 gap-5">
          {engines.map((e, i) => (
            <Reveal key={e.code} delay={i * 100}>
              <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: e.accent }}>
                <div className="flex items-start gap-5">
                  <div
                    className="shrink-0 grid place-items-center h-14 w-14 rounded-2xl font-mono-num font-bold text-base"
                    style={{ background: `linear-gradient(135deg, ${e.accent}33, ${e.accent}11)`, color: e.accent, boxShadow: `inset 0 0 0 1px ${e.accent}50` }}
                  >
                    {e.code}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold leading-snug mb-2">{e.name}</h3>
                    <p className="text-[0.9rem] leading-relaxed text-[rgb(var(--fg-muted))]">{e.body}</p>
                    <Link href={e.href} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: e.accent }}>
                      Explore the {e.code} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <GlassCard pad="md" tone="plain" className="text-center text-sm text-[rgb(var(--fg-muted))]">
            <strong className="text-[rgb(var(--fg))]">Engine Interdependency:</strong> The OSE provides behavioral data to the CRE, the CRE calibrates emotional tone for the NGE, the NGE generates content for the Echo Chamber, and the Echo Chamber feeds amplification metrics back to the OSE. Continuous closed-loop optimization.
          </GlassCard>
        </Reveal>
      </Container>
    </Section>
  );
}

/* ============================================================
   9. DEPLOYMENT TIERS
   ============================================================ */
function DeploymentTiersSection() {
  return (
    <Section id="deployment-tiers">
      <Container>
        <SectionHeading
          eyebrow="Deployment Tiers"
          icon="Layers"
          title="Scalable From Individual Protection to Sovereign Operations"
          subtitle="Four deployment tiers calibrated for specific operational scales — every tier maintains 95-98% organic authenticity."
        />

        <Reveal className="mt-12">
          <GlassCard pad="lg" tone="glow" className="overflow-x-auto p-0">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-[rgb(var(--border)/0.18)]">
                  <th className="text-left font-semibold px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Tier</th>
                  <th className="text-left font-semibold px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Personas</th>
                  <th className="text-left font-semibold px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Scale</th>
                  <th className="text-left font-semibold px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Use Case</th>
                  <th className="text-left font-semibold px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))]">Risk</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => (
                  <tr key={t.name} className="border-b border-[rgb(var(--border)/0.08)] last:border-0 hover:bg-[rgb(var(--border)/0.04)] transition-colors">
                    <td className="px-5 py-4 font-bold font-mono-num" style={{ color: t.accent }}>{t.name}</td>
                    <td className="px-5 py-4 font-mono-num">{t.personas}</td>
                    <td className="px-5 py-4">{t.scale}</td>
                    <td className="px-5 py-4 text-[rgb(var(--fg-muted))]">{t.useCase}</td>
                    <td className="px-5 py-4 text-[rgb(var(--fg-muted))]">{t.risk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlassCard>
        </Reveal>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t, i) => (
            <Reveal key={t.name + "-card"} delay={i * 80}>
              <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: t.accent }}>
                <h3 className="font-bold font-mono-num" style={{ color: t.accent }}>{t.name}</h3>
                <div className="mt-2 text-[0.85rem] text-[rgb(var(--fg-muted))]">
                  {t.personas} personas · {t.scale} scale
                </div>
                <p className="mt-2 text-[0.82rem] leading-relaxed text-[rgb(var(--fg-subtle))]">
                  {t.useCase}
                </p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   10. KEY METRICS (bar chart)
   ============================================================ */
function KeyMetrics() {
  return (
    <Section id="key-metrics">
      <Container>
        <SectionHeading
          eyebrow="Key Metrics"
          icon="BarChart3"
          title="Performance That Speaks for Itself"
          subtitle="Every metric is documented, verified, and consistent across 300+ elite engagements."
        />

        <Reveal className="mt-12">
          <GlassCard pad="lg" tone="glow" className="h-[460px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.slice(0, 10)} layout="vertical" margin={{ top: 8, right: 32, left: 8, bottom: 8 }}>
                <XAxis type="number" hide domain={[0, 100]} />
                <YAxis
                  type="category"
                  dataKey="label"
                  tick={{ fill: "rgb(var(--fg-muted))", fontSize: 11, fontWeight: 500 }}
                  width={150}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgb(var(--border) / 0.06)" }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload as MetricRow;
                    return (
                      <div className="rounded-xl glass p-3 text-xs shadow-xl">
                        <div className="font-semibold mb-0.5">{d.label}</div>
                        <div className="font-mono-num font-bold" style={{ color: d.accent }}>{d.value}</div>
                        <div className="text-[rgb(var(--fg-muted))] mt-1 max-w-[200px]">{d.context}</div>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="progress" radius={[0, 6, 6, 0]}>
                  {metrics.slice(0, 10).map((m, i) => (
                    <Cell key={i} fill={m.accent} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GlassCard>
        </Reveal>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {metrics.slice(10).map((m) => (
            <Reveal key={m.label}>
              <div className="card-glow rounded-xl glass p-4 h-full" style={{ ["--glow" as string]: m.accent }}>
                <div className="text-xl font-bold font-mono-num leading-none" style={{ color: m.accent }}>{m.value}</div>
                <div className="mt-1.5 text-xs font-semibold">{m.label}</div>
                <div className="mt-0.5 text-[0.7rem] text-[rgb(var(--fg-subtle))] leading-snug">{m.context}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   11. CORE FEATURES
   ============================================================ */
function Features() {
  const accents = ["var(--color-cyan)", "var(--color-purple)", "var(--color-emerald)", "var(--color-amber)", "var(--color-crimson)", "var(--color-denim)", "var(--color-coral)", "var(--color-violet)"];
  return (
    <Section id="core-features">
      <Container>
        <SectionHeading
          eyebrow="Core Features"
          icon="Sparkles"
          title="Built for Precision, Scale, and Authenticity"
          subtitle="Eight engineered capabilities, each battle-tested across hundreds of deployments."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={(i % 4) * 60}>
              <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: accents[i % accents.length] }}>
                <Icon name={f.icon} className="h-6 w-6 mb-3" style={{ color: accents[i % accents.length] }} />
                <h3 className="font-semibold text-base leading-snug mb-1.5">{f.title}</h3>
                <p className="text-[0.85rem] leading-relaxed text-[rgb(var(--fg-muted))]">{f.body}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   12. ADVANCED FEATURES
   ============================================================ */
function AdvancedFeatures() {
  return (
    <Section id="advanced-features">
      <Container>
        <SectionHeading
          eyebrow="Advanced Features"
          icon="Code2"
          title="Technical Depth for Enterprise Evaluators"
          subtitle="Underlying architecture, security model, and the engineering that powers every deployment."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {advancedFeatures.map((f, i) => {
            const accents = ["var(--color-cyan)", "var(--color-purple)", "var(--color-emerald)", "var(--color-amber)", "var(--color-crimson)", "var(--color-denim)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={f.title} delay={(i % 3) * 80}>
                <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <Icon name={f.icon} className="h-7 w-7 mb-4" style={{ color: a }} />
                  <h3 className="text-lg font-semibold leading-snug mb-2">{f.title}</h3>
                  <p className="text-[0.88rem] leading-relaxed text-[rgb(var(--fg-muted))]">{f.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   13. THE PROCESS
   ============================================================ */
function ProcessSection() {
  return (
    <Section id="process">
      <Container>
        <SectionHeading
          eyebrow="The Process"
          icon="Activity"
          title="From Onboarding to Operational Dominance"
          subtitle="Structured engagement lifecycle designed to deliver measurable outcomes from day one."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {process.map((p, i) => {
            const accents = ["var(--color-cyan)", "var(--color-emerald)", "var(--color-purple)", "var(--color-amber)", "var(--color-crimson)", "var(--color-denim)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={p.title} delay={i * 60}>
                <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <div className="text-[0.7rem] uppercase tracking-[0.2em] font-semibold mb-1" style={{ color: a }}>{p.week}</div>
                  <h3 className="text-lg font-bold leading-snug mb-2">{p.title}</h3>
                  <p className="text-[0.88rem] leading-relaxed text-[rgb(var(--fg-muted))]">{p.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   14. DELIVERABLES
   ============================================================ */
function Deliverables() {
  return (
    <Section id="deliverables">
      <Container>
        <SectionHeading
          eyebrow="Deliverables"
          icon="FileText"
          title="What You Receive"
          subtitle="Tangible, measurable deliverables from every Perception X2 engagement."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {deliverables.map((d, i) => {
            const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-coral)", "var(--color-denim)", "var(--color-violet)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={d.title} delay={(i % 4) * 60}>
                <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <Icon name={d.icon} className="h-6 w-6 mb-3" style={{ color: a }} />
                  <h3 className="font-semibold text-base leading-snug mb-1.5">{d.title}</h3>
                  <p className="text-[0.85rem] leading-relaxed text-[rgb(var(--fg-muted))]">{d.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   15. OUTCOMES
   ============================================================ */
function Outcomes() {
  return (
    <Section id="outcomes">
      <Container>
        <SectionHeading
          eyebrow="Outcomes"
          icon="TrendingUp"
          title="Measurable Results, Not Aspirations"
          subtitle="Documented, repeatable, verified across 300+ elite engagements."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {outcomes.map((o, i) => (
            <Reveal key={o.label} delay={i * 80}>
              <div className="card-glow rounded-2xl glass glass-card-hover p-7 h-full" style={{ ["--glow" as string]: o.accent }}>
                <div className="text-[clamp(2rem,5vw,3rem)] font-bold font-mono-num leading-none tracking-tight" style={{ color: o.accent }}>
                  {o.value}
                </div>
                <p className="mt-3 text-[0.95rem] text-[rgb(var(--fg-muted))] leading-relaxed">{o.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   16. BENEFITS
   ============================================================ */
function Benefits() {
  const accents = ["var(--color-cyan)", "var(--color-emerald)", "var(--color-purple)", "var(--color-amber)", "var(--color-crimson)", "var(--color-denim)", "var(--color-coral)", "var(--color-violet)"];
  return (
    <Section id="benefits">
      <Container>
        <SectionHeading
          eyebrow="Benefits"
          icon="BadgeCheck"
          title="What Perception X2 Means for You"
          subtitle="Eight distinct advantages that compound with every engagement."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((b, i) => (
            <Reveal key={b.title} delay={(i % 4) * 60}>
              <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: accents[i % accents.length] }}>
                <Icon name={b.icon} className="h-6 w-6 mb-3" style={{ color: accents[i % accents.length] }} />
                <h3 className="font-semibold text-base leading-snug mb-1.5">{b.title}</h3>
                <p className="text-[0.85rem] leading-relaxed text-[rgb(var(--fg-muted))]">{b.body}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   17. USPs
   ============================================================ */
function Usps() {
  return (
    <Section id="usps">
      <Container>
        <SectionHeading
          eyebrow="Our USPs"
          icon="Award"
          title="What Sets Perception X2 Apart"
          subtitle="Six differentiators that competitors cannot replicate."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {usps.map((u, i) => {
            const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-denim)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={u.title} delay={(i % 3) * 80}>
                <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <div className="h-1 w-12 rounded-full mb-3" style={{ background: a }} />
                  <h3 className="text-base font-bold leading-snug mb-2">{u.title}</h3>
                  <p className="text-[0.88rem] leading-relaxed text-[rgb(var(--fg-muted))]">{u.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   18. AUDIENCES (Who It's For)
   ============================================================ */
function Audiences() {
  return (
    <Section id="who-its-for">
      <Container>
        <SectionHeading
          eyebrow="Who It's For"
          icon="Users"
          title="Built for Organizations Where Perception Determines Outcome"
          subtitle="Eight audience profiles, each calibrated for the perception challenges they face."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {audiences.map((a, i) => {
            const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-coral)", "var(--color-denim)", "var(--color-violet)"];
            const ac = accents[i % accents.length];
            return (
              <Reveal key={a.title} delay={(i % 4) * 60}>
                <GlassCard pad="md" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: ac }}>
                  <Icon name={a.icon} className="h-6 w-6 mb-3" style={{ color: ac }} />
                  <h3 className="font-semibold text-base leading-snug mb-1.5">{a.title}</h3>
                  <p className="text-[0.85rem] leading-relaxed text-[rgb(var(--fg-muted))]">{a.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   19. INDUSTRIES
   ============================================================ */
function Industries() {
  return (
    <Section id="industries">
      <Container>
        <SectionHeading
          eyebrow="Industry Applications"
          icon="Building2"
          title="Perception Management Across Every Sector"
          subtitle="Eight industry verticals, each with calibrated deployment playbooks."
        />
        <div className="mt-14 grid sm:grid-cols-2 gap-5">
          {industries.map((ind, i) => {
            const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-coral)", "var(--color-denim)", "var(--color-violet)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={ind.title} delay={(i % 2) * 100}>
                <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <div className="flex items-start gap-4">
                    <span
                      className="grid place-items-center h-12 w-12 rounded-xl shrink-0"
                      style={{ background: `linear-gradient(135deg, ${a}26, ${a}0a)`, boxShadow: `inset 0 0 0 1px ${a}40` }}
                    >
                      <Icon name={ind.icon} className="h-5 w-5" style={{ color: a }} />
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold leading-snug mb-2">{ind.title}</h3>
                      <p className="text-[0.9rem] leading-relaxed text-[rgb(var(--fg-muted))]">{ind.body}</p>
                    </div>
                  </div>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   20. WHY CHOOSE US
   ============================================================ */
function WhyChooseUs() {
  return (
    <Section id="why-choose-us">
      <Container>
        <SectionHeading
          eyebrow="Why Choose Us"
          icon="Trophy"
          title="The Choice That Separates Leaders from Followers"
          subtitle="Six reasons elite organizations choose Perception X2 over every alternative."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {whyChooseUs.map((w, i) => {
            const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-emerald)", "var(--color-amber)", "var(--color-denim)"];
            const a = accents[i % accents.length];
            return (
              <Reveal key={w.title} delay={(i % 3) * 80}>
                <GlassCard pad="lg" tone="glow" className="h-full card-glow" style={{ ["--glow" as string]: a }}>
                  <h3 className="text-base font-bold leading-snug mb-2">{w.title}</h3>
                  <p className="text-[0.88rem] leading-relaxed text-[rgb(var(--fg-muted))]">{w.body}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   21. FAQ (accordion)
   ============================================================ */
function FAQ() {
  return (
    <Section id="faq">
      <Container>
        <SectionHeading
          eyebrow="FAQ"
          icon="HelpCircle"
          title="Frequently Asked Questions"
          subtitle="Everything elite clients ask before engagement."
        />
        <div className="mt-12 max-w-4xl mx-auto grid gap-3">
          {faq.map((f, i) => (
            <Reveal key={f.q} delay={i * 30}>
              <FAQItem q={f.q} a={f.a} />
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   22. PAA
   ============================================================ */
function PAA() {
  return (
    <Section id="paa">
      <Container>
        <SectionHeading
          eyebrow="People Also Ask"
          icon="MessageCircle"
          title="What Others Are Asking"
          subtitle="Common perception management questions, answered concisely."
        />
        <div className="mt-12 max-w-4xl mx-auto grid gap-3">
          {paa.map((p, i) => (
            <Reveal key={p.q} delay={i * 30}>
              <FAQItem q={p.q} a={p.a} />
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* Reusable FAQ accordion item */
function FAQItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="group glass rounded-2xl overflow-hidden">
      <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none hover:bg-[rgb(var(--border)/0.04)] transition-colors">
        <span className="font-semibold text-[0.97rem] leading-snug">{q}</span>
        <span className="grid place-items-center h-7 w-7 rounded-full shrink-0 bg-[rgb(var(--border)/0.1)] transition-transform duration-300 group-open:rotate-45">
          <span className="text-lg leading-none">+</span>
        </span>
      </summary>
      <div className="px-5 pb-4 text-[0.92rem] leading-relaxed text-[rgb(var(--fg-muted))]">
        {a}
      </div>
    </details>
  );
}

/* ============================================================
   23. CTA
   ============================================================ */
function CTA() {
  return (
    <Section id="contact" className="!py-0">
      <Container>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass p-10 sm:p-14 text-center">
            <div className="pointer-events-none absolute inset-0 -z-10">
              <div className="absolute top-0 left-1/4 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-30 blur-[120px] animate-pulse-glow" />
              <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-25 blur-[130px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            </div>
            <h2 className="text-balance text-[clamp(1.75rem,4vw,2.75rem)] font-bold leading-[1.05] tracking-[-0.02em] max-w-3xl mx-auto">
              {ctaSection.title}
            </h2>
            <p className="mt-5 text-[1rem] sm:text-[1.05rem] leading-relaxed text-[rgb(var(--fg-muted))] max-w-3xl mx-auto">
              {ctaSection.body}
            </p>
            <p className="mt-4 text-[0.92rem] text-[rgb(var(--fg-subtle))] italic max-w-2xl mx-auto">
              {ctaSection.tagline}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link href="/contact" className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base">
                Request a Demo <ArrowRight className="h-4 w-4" />
              </Link>
              <Link href="/contact" className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base">
                Schedule a Consultation
              </Link>
              <Link href="/platform" className="text-sm font-semibold text-[rgb(var(--fg-muted))] hover:text-[rgb(var(--fg))] underline-offset-4 hover:underline">
                Explore the Platform →
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[rgb(var(--fg-subtle))]">
              {ctaSection.indicators.map((ind, i) => (
                <span key={ind} className="flex items-center gap-2">
                  {i > 0 && <span className="h-1 w-1 rounded-full bg-[rgb(var(--border))]" />}
                  <span className="font-mono-num font-semibold text-[rgb(var(--fg))]">{ind}</span>
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Related pages */}
        <div className="mt-12">
          <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-[rgb(var(--fg-subtle))] mb-4">Related Pages</h3>
          <div className="flex flex-wrap gap-2">
            {relatedPages.map((r) => (
              <Link
                key={r.href}
                href={r.href}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs font-medium hover:bg-[rgb(var(--border)/0.1)] transition-colors"
              >
                <Icon name={r.icon} className="h-3 w-3" />
                {r.label}
              </Link>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}

/* ============================================================
   METRIC TYPE IMPORT (used in chart tooltip)
   ============================================================ */
type MetricRow = { label: string; value: string; context: string; progress: number; accent: string };

/* ============================================================
   PAGE EXPORT
   ============================================================ */
export function PlatformOverviewPage() {
  return (
    <>
      <Hero />
      <ExecutiveSummary />
      <Problem />
      <Solution />
      <PlatformOverviewSection />
      <HowItWorksSection />
      <Capabilities />
      <Engines />
      <DeploymentTiersSection />
      <KeyMetrics />
      <Features />
      <AdvancedFeatures />
      <ProcessSection />
      <Deliverables />
      <Outcomes />
      <Benefits />
      <Usps />
      <Audiences />
      <Industries />
      <WhyChooseUs />
      <FAQ />
      <PAA />
      <CTA />
    </>
  );
}
